import { useCallback, useState, useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import { IPaginatedDataDTO } from 'src/api/dtos';
import { ApiParamsType, UseQueryType } from 'src/shared/types';

import { usePrevious } from './usePrevious';

interface Props {
  useQuery: UseQueryType;
  skip?: boolean;
  pollingInterval?: number;
  apiParams?: ApiParamsType;
  onParamsChange?: (params?: Record<string, any>) => void;
  onEndReached?: () => void;
  overridePerPage?: {
    initialPerPage: number;
    defaultPerPage: number;
  };
}

export const useInfiniteScroll = <T>({
  useQuery,
  skip,
  pollingInterval = 0,
  apiParams = { page: 1 },
  onParamsChange,
  overridePerPage,
  ...props
}: Props) => {
  const [queryParams, setQueryParams] = useState<ApiParamsType>(apiParams);
  const [collection, setCollection] = useState<T[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  // Check needed as queryParams is one render behind apiParams (can be out of sync if skip depends on apiParams)
  const paramsAreEqual = isEqual(omit(apiParams, ['page']), omit(queryParams, ['page']));

  const { initialPerPage, defaultPerPage } = overridePerPage ?? {};

  const {
    data: paginatedData,
    isLoading,
    isFetching,
    isUninitialized,
    error,
    refetch,
  } = useQuery(
    skip || !paramsAreEqual
      ? skipToken
      : {
          ...queryParams,
          // Override the per page for the first page
          perPage:
            initialPerPage && (queryParams.page === 1 || !queryParams.page) ? initialPerPage : queryParams.perPage,
        },
    { skip, pollingInterval },
  );

  const previousPaginatedData = usePrevious<IPaginatedDataDTO<unknown> | undefined>(paginatedData);

  useEffect(() => {
    if (apiParams && paramsAreEqual) {
      return;
    }

    setQueryParams(cloneDeep(apiParams));
  }, [apiParams]);

  useEffect(() => {
    if (isEqual(previousPaginatedData, paginatedData)) {
      return;
    }

    const defaultList = paginatedData?.data ?? [];
    const list = paginatedData && paginatedData.page > 1 ? [...cloneDeep(collection), ...defaultList] : defaultList;

    setCollection(list);
    setIsLoadingMore(false);
  }, [isLoadingMore, paginatedData]);

  const handleLoadMore = useCallback(() => {
    if (isLoadingMore || !paginatedData) {
      return;
    }

    const hasMore = paginatedData.page * paginatedData.perPage < paginatedData.total;

    if (hasMore) {
      const nextPerPage = queryParams.perPage || defaultPerPage || paginatedData.perPage;
      const currentPage = paginatedData.to / nextPerPage;

      const newParams: ApiParamsType = {
        ...queryParams,
        page: currentPage + 1,
      };

      if (onParamsChange) {
        onParamsChange(newParams);
      }

      setQueryParams(newParams);
      setIsLoadingMore(true);

      if (props.onEndReached) {
        props.onEndReached();
      }
    }
  }, [paginatedData, queryParams, isLoadingMore, props.onEndReached]);

  const handleRefreshMore = useCallback(() => {
    refetch();
    setIsLoadingMore(true);
  }, [refetch]);

  const isRefreshing = (isLoading || isFetching) && !isLoadingMore;

  return {
    collection,
    setCollection,
    paginatedData,
    handleLoadMore,
    handleRefreshMore,
    isUninitialized,
    isLoadingMore,
    isRefreshing,
    error,
    refetch,
  };
};
